/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/HelpContactInformation/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import styles from "./HelpContactInformation.module.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ReactComponent as CopyToClipboardIcon } from "../../assets/icons/Help/CopyToClipboardIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/SideMenu/CheckCircleIcon.svg";
import { getCopyButtonAriaLabel } from "./helpContactCopyUtils.js";

const HelpContactInformation = () => {
  const { t } = useTranslation();
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => {
        setCopiedField(null);
      }, 2000);
    } catch (err) {
      logger.error("Failed to copy text: ", { err: sanitizeError(err) });
    }
  };

  const nameValue = t("components.helpContactInformation.nameValue");
  const phoneValue = t("components.helpContactInformation.phoneValue");
  const emailValue = t("components.helpContactInformation.emailValue");
  const addressValue = t("components.helpContactInformation.addressValue");

  return (
    <div className={styles.contactContainer}>
      <div className={styles.contactHeader}>
        <h2 className={styles.contactTitle}>
          {t("components.helpContactInformation.title")}
        </h2>
        <div className={styles.border}></div>
      </div>

      <div className={styles.contactContent}>
        <div className={styles.contactRow}>
          <span className={styles.contactLabel}>
            {t("components.helpContactInformation.nameLabel")}:
          </span>
          <div className={styles.contactValueContainer}>
            <span className={styles.contactValue}>{nameValue}</span>
            <button
              className={styles.copyButton}
              onClick={() => copyToClipboard(nameValue, "name")}
              aria-label={getCopyButtonAriaLabel(copiedField, "name", t)}
            >
              {copiedField === "name" ? (
                <CheckCircleIcon className={styles.checkIcon} />
              ) : (
                <CopyToClipboardIcon className={styles.copyIcon} />
              )}
            </button>
          </div>
        </div>

        <div className={styles.contactRow}>
          <span className={styles.contactLabel}>
            {t("components.helpContactInformation.phoneLabel")}:
          </span>
          <div className={styles.contactValueContainer}>
            <span className={styles.contactValue}>{phoneValue}</span>
            <button
              className={styles.copyButton}
              onClick={() => copyToClipboard(phoneValue, "phone")}
              aria-label={getCopyButtonAriaLabel(copiedField, "phone", t)}
            >
              {copiedField === "phone" ? (
                <CheckCircleIcon className={styles.checkIcon} />
              ) : (
                <CopyToClipboardIcon className={styles.copyIcon} />
              )}
            </button>
          </div>
        </div>

        <div className={styles.contactRow}>
          <span className={styles.contactLabel}>
            {t("components.helpContactInformation.emailLabel")}:
          </span>
          <div className={styles.contactValueContainer}>
            <span className={styles.contactValue}>{emailValue}</span>
            <button
              className={styles.copyButton}
              onClick={() => copyToClipboard(emailValue, "email")}
              aria-label={getCopyButtonAriaLabel(copiedField, "email", t)}
            >
              {copiedField === "email" ? (
                <CheckCircleIcon className={styles.checkIcon} />
              ) : (
                <CopyToClipboardIcon className={styles.copyIcon} />
              )}
            </button>
          </div>
        </div>

        <div className={styles.contactRow}>
          <span className={styles.contactLabel}>
            {t("components.helpContactInformation.addressLabel")}:
          </span>
          <div
            className={`${styles.contactValueContainer} ${styles.addressContainer}`}
          >
            <span className={styles.contactValue}>{addressValue}</span>
            <button
              className={styles.copyButton}
              onClick={() => copyToClipboard(addressValue, "address")}
              aria-label={getCopyButtonAriaLabel(copiedField, "address", t)}
            >
              {copiedField === "address" ? (
                <CheckCircleIcon className={styles.checkIcon} />
              ) : (
                <CopyToClipboardIcon className={styles.copyIcon} />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpContactInformation;
